import { faCheck, faCheckCircle, faTrashAlt } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { format } from 'date-fns'
import React from 'react'
import { Dialog, DialogTrigger } from 'react-aria-components'
import { useTranslation } from 'react-i18next'

import { UserRecipePhoto } from '@/api/types'
import { useAuth } from '@/auth'
import { Button } from '@/components/Button'
import { Flex } from '@/components/Flex'
import { Modal, ModalContent, ModalHeader } from '@/components/Modal'
import PhotoPicker from './PhotoPicker'
import './RecipePhotoItem.css'

type Props = {
  idRecipe: number
  photo: UserRecipePhoto
  onApprove: () => void
  onDelete: (photo: UserRecipePhoto) => void
  onReject: () => void
  onSetAsCover: () => void
}

export default function RecipePhotoItem({
  idRecipe,
  photo,
  onApprove,
  onDelete,
  onReject,
  onSetAsCover,
}: Props) {
  const { t } = useTranslation()
  const auth = useAuth()

  if (photo.idRecipePhoto === -1) {
    return <PhotoPicker idRecipe={idRecipe} />
  }

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation()

    if (confirm(t('photos.confirmReject'))) {
      onDelete(photo)
    }
  }

  return (
    <div className="RecipePhotoItem">
      <DialogTrigger>
        <div
          className="RecipePhotoItem-triggerWrap"
          data-in-moderation={photo.latestModerationStatus === 'NOT_MODERATED'}>
          <Button className="RecipePhotoItem-trigger" data-is-removed="false">
            <img key={photo.idRecipePhoto} src={photo.photoURL} />
          </Button>

          {photo.photoSource === 'AI_GENERATED' && (
            <div className="RecipePhotoItem-ai-badge">AI</div>
          )}

          {photo.isCover && (
            <div className="RecipePhotoItem-is-cover">
              <FontAwesomeIcon icon={faCheck} size="xs" color="white" />
            </div>
          )}

          <button className="RecipePhotoUpload-remove" onClick={handleDeleteClick} type="button">
            <FontAwesomeIcon icon={faTrashAlt} color="var(--invalid-color)" />
          </button>
        </div>
        <Modal style={{ width: 700 }}>
          <Dialog>
            {({ close }) => (
              <>
                <ModalHeader onClose={close}>{t('photos.photo')}</ModalHeader>
                <ModalContent>
                  <img src={photo.photoURL} alt="Photo" style={{ width: '100%' }} />

                  <Flex justify="space-between">
                    <div className="RecipePhotoItem-info">
                      <div className="RecipePhotoItem-label">{t('photos.createdBy')}</div>
                      <div className="RecipePhotoItem-value">
                        {auth.email || `#${photo.authorId}`},{' '}
                        {format(photo.creationDate, 'dd MMM yyyy')}
                      </div>
                    </div>

                    {photo.latestModerationStatus === 'NOT_MODERATED' && (
                      <Flex gap={4}>
                        <Button
                          variant="negative"
                          onPress={() => {
                            if (confirm(t('photos.confirmReject'))) {
                              close()
                              onReject()
                            }
                          }}>
                          {t('photos.reject')}
                        </Button>
                        <Button
                          onPress={() => {
                            close()
                            onApprove()
                          }}>
                          {t('photos.approve')}
                        </Button>
                        <Button
                          style={{ gap: 4, display: 'flex', alignItems: 'center' }}
                          onPress={() => {
                            close()
                            onSetAsCover()
                          }}>
                          <FontAwesomeIcon icon={faCheckCircle} color="#62A3FF" />
                          {t('photos.setAsCover')}
                        </Button>
                      </Flex>
                    )}

                    {(photo.latestModerationStatus === 'APPROVED' ||
                      photo.latestModerationStatus === 'AUTOMATICALLY_APPROVED') && (
                      <Button
                        style={{ gap: 4, display: 'flex', alignItems: 'center' }}
                        onPress={() => {
                          close()
                          onSetAsCover()
                        }}>
                        <FontAwesomeIcon icon={faCheckCircle} color="#62A3FF" />
                        {t('photos.setAsCover')}
                      </Button>
                    )}
                  </Flex>
                </ModalContent>
              </>
            )}
          </Dialog>
        </Modal>
      </DialogTrigger>
    </div>
  )
}
